import { useEffect, useMemo, useRef, useState } from 'react'
import { Channel as StreamChannel, Event, StreamChat } from 'stream-chat'
import { App } from '@capacitor/app'
import { PluginListenerHandle } from '@capacitor/core'
import Bugsnag from '@bugsnag/js'
import { t } from 'ttag'
import { UserId } from '../../../services/api.types'
import { getChatClient, channelType } from '../../ChallengeChat/hooks/useInitChat'
import { groupChannelType } from '../../../components/YumChatView/useChatChannel'
import { translateSystemMessage } from '../../../components/YumChatView/YumSystemMessage'

export interface ChatListEntryInput {
  kind: 'challenge' | 'group'
  id: string
  name: string
  // App-side fallback avatar (e.g. challenge logo), used when Stream has no channel image.
  image?: string
  /** Challenge end timestamp (UTC ms); ended challenges group into a "Past challenges" section. */
  endedAt?: number
}

export interface ChatListEntry extends ChatListEntryInput {
  cid: string
  image?: string
  lastMessageText?: string
  lastMessageAt?: number
  unreadCount: number
}

export interface ChannelMeta {
  image?: string
  lastMessageText?: string
  lastMessageAt?: number
  unreadCount: number
}

// Stream allows querying at most 30 channels per request.
const QUERY_CHANNELS_LIMIT = 30

export const entryCid = (entry: ChatListEntryInput) =>
  `${entry.kind === 'challenge' ? channelType : groupChannelType}:${entry.id}`

const extractMeta = (channel: StreamChannel): ChannelMeta => {
  const messages = channel.state.latestMessages?.length ? channel.state.latestMessages : channel.state.messages
  const lastMessage = messages && messages.length > 0 ? messages[messages.length - 1] : undefined
  let lastMessageText: string | undefined
  if (lastMessage && lastMessage.type !== 'deleted') {
    lastMessageText =
      translateSystemMessage(lastMessage as any) ||
      lastMessage.text ||
      (lastMessage.attachments?.length ? t`Attachment` : undefined)
  }
  return {
    // @ts-ignore // image exists on channel data
    image: channel.data?.image,
    lastMessageText,
    lastMessageAt: channel.state.last_message_at ? channel.state.last_message_at.getTime() : undefined,
    unreadCount: channel.countUnread(),
  }
}

// Lists the user's chat channels (from app data) enriched with Stream state where available.
// Channels the user has never opened may not be provisioned on Stream yet — those rows simply
// stay un-enriched (no preview/unread); opening them still works because the chat views join on open.
// `cachedMeta` (the redux-persisted snapshot of a previous enrichment) makes the list paint
// instantly — and offline — with the last known previews while a fresh enrichment runs in the
// background; `onMetaChange` reports every update back so the cache stays current.
export const useChatChannelList = (
  userId: UserId,
  name: string,
  inputs: ChatListEntryInput[],
  activeCid?: string,
  cachedMeta?: Record<string, ChannelMeta>,
  onMetaChange?: (meta: Record<string, ChannelMeta>) => void,
) => {
  const hasCache = !!cachedMeta && Object.keys(cachedMeta).length > 0
  const [metaByCid, setMetaByCid] = useState<Record<string, ChannelMeta>>(() => (hasCache ? cachedMeta! : {}))
  // True once the first enrichment attempt has finished (successfully or not). The list is not
  // rendered before that — painting the un-enriched list first would visibly re-sort a moment
  // later when the Stream data arrives. A cached snapshot counts: its order is already the
  // final one from the last session, so it can paint immediately.
  const [hasEnriched, setHasEnriched] = useState(hasCache)
  const clientRef = useRef<StreamChat>()

  // Report meta updates to the cache — but not the initial value, which came FROM the cache.
  const initialMetaRef = useRef(metaByCid)
  const onMetaChangeRef = useRef(onMetaChange)
  onMetaChangeRef.current = onMetaChange
  useEffect(() => {
    if (metaByCid !== initialMetaRef.current) onMetaChangeRef.current?.(metaByCid)
  }, [metaByCid])

  // The conversation open next to the list (desktop two-pane); its unread stays zeroed.
  const activeCidRef = useRef<string | undefined>(activeCid)
  activeCidRef.current = activeCid

  const cids = useMemo(() => inputs.map(entryCid), [inputs])
  // The event handlers live in a once-per-user effect; a ref keeps their cid filter current.
  const cidsRef = useRef<string[]>(cids)
  cidsRef.current = cids

  useEffect(() => {
    if (!activeCid) return
    setMetaByCid((prev) => {
      const existing = prev[activeCid]
      if (!existing || !existing.unreadCount) return prev
      return { ...prev, [activeCid]: { ...existing, unreadCount: 0 } }
    })
  }, [activeCid])

  useEffect(() => {
    if (!userId) {
      return
    }

    let cancelled = false

    const enrich = async (client: StreamChat) => {
      try {
        // A single request for the user's most recently active member channels of both kinds.
        // Querying by membership (instead of cid $in over the whole app-data list) keeps this
        // one request regardless of how many chats the user has — a cid $in sweep needs
        // ceil(n / 30) requests and trips Stream's per-user rate limit on large accounts.
        // Channels beyond the most recent QUERY_CHANNELS_LIMIT stay un-enriched, which is
        // where they would sort anyway.
        const [channels, unreadResponse] = await Promise.all([
          client.queryChannels(
            // @ts-ignore // type $in is a valid Stream filter
            { type: { $in: [channelType, groupChannelType] }, members: { $in: [userId] } },
            { last_message_at: -1 },
            { limit: QUERY_CHANNELS_LIMIT, state: true, watch: false },
          ),
          // Unread counts come from the dedicated endpoint — the same source the nav badge
          // saga uses (ChatSagas) — so the row dots always agree with the badge. It also
          // covers channels beyond the QUERY_CHANNELS_LIMIT enrichment window.
          client.getUnreadCount().catch(() => null),
        ])
        const meta: Record<string, ChannelMeta> = {}
        channels.forEach((channel) => {
          if (channel.cid) meta[channel.cid] = extractMeta(channel)
        })
        ;(unreadResponse?.channels || []).forEach((channel) => {
          const cid = channel.channel_id
          const unreadCount = channel.unread_count || 0
          if (meta[cid]) meta[cid].unreadCount = unreadCount
          else if (cidsRef.current.includes(cid)) meta[cid] = { unreadCount }
        })
        if (!cancelled) setMetaByCid(meta)
      } catch (e: any) {
        // Non-fatal: the list still renders from app data without previews/unread counts.
        console.error('useChatChannelList enrich error: ', e)
        Bugsnag.notify(e)
      } finally {
        if (!cancelled) setHasEnriched(true)
      }
    }

    const handleNewMessage = (event: Event) => {
      if (cancelled || !event.cid || !cidsRef.current.includes(event.cid)) return
      const client = clientRef.current
      setMetaByCid((prev) => {
        const existing = prev[event.cid!] || { unreadCount: 0 }
        const isOwnMessage = !!event.user?.id && event.user.id === client?.userID
        const isActive = event.cid === activeCidRef.current
        return {
          ...prev,
          [event.cid!]: {
            ...existing,
            lastMessageText:
              translateSystemMessage(event.message as any) ||
              event.message?.text ||
              (event.message?.attachments?.length ? t`Attachment` : existing.lastMessageText),
            lastMessageAt: event.message?.created_at ? new Date(event.message.created_at).getTime() : Date.now(),
            unreadCount: isActive ? 0 : isOwnMessage ? existing.unreadCount : existing.unreadCount + 1,
          },
        }
      })
    }

    const handleMarkRead = (event: Event) => {
      if (cancelled || !event.cid || !cidsRef.current.includes(event.cid)) return
      const client = clientRef.current
      // @ts-ignore // activeChannels is available on the client
      const channel = client?.activeChannels?.[event.cid]
      const unreadCount = channel ? channel.countUnread() : 0
      setMetaByCid((prev) => {
        const existing = prev[event.cid!]
        if (!existing || existing.unreadCount === unreadCount) return prev
        return { ...prev, [event.cid!]: { ...existing, unreadCount } }
      })
    }

    let appStateListener: PluginListenerHandle | undefined

    const init = async () => {
      try {
        const client = await getChatClient(userId, name)
        if (!client || cancelled) {
          if (!cancelled) setHasEnriched(true)
          return
        }
        clientRef.current = client

        client.on('message.new', handleNewMessage)
        client.on('notification.message_new', handleNewMessage)
        client.on('notification.mark_read', handleMarkRead)

        appStateListener = await App.addListener('appStateChange', async ({ isActive }) => {
          if (!isActive || cancelled) return
          try {
            // The singleton client is shared; another component may have closed the connection.
            if (client.userID) {
              await client.openConnection()
              await client.recoverState()
            }
            await enrich(client)
          } catch (e: any) {
            console.error('useChatChannelList foreground recovery error: ', e)
          }
        })

        await enrich(client)
      } catch (e: any) {
        console.error('useChatChannelList init error: ', e)
        Bugsnag.notify(e)
        if (!cancelled) setHasEnriched(true)
      }
    }

    init()

    return () => {
      cancelled = true
      appStateListener?.remove()
      clientRef.current?.off('message.new', handleNewMessage)
      clientRef.current?.off('notification.message_new', handleNewMessage)
      clientRef.current?.off('notification.mark_read', handleMarkRead)
    }
  }, [userId])

  const entries: ChatListEntry[] = useMemo(() => {
    const merged = inputs.map((input) => {
      const cid = entryCid(input)
      const meta = metaByCid[cid]
      return {
        ...input,
        cid,
        image: meta?.image || input.image,
        lastMessageText: meta?.lastMessageText,
        lastMessageAt: meta?.lastMessageAt,
        unreadCount: meta?.unreadCount || 0,
      }
    })
    return merged.sort((a, b) => {
      if (a.lastMessageAt && b.lastMessageAt) return b.lastMessageAt - a.lastMessageAt
      if (a.lastMessageAt) return -1
      if (b.lastMessageAt) return 1
      return a.name.localeCompare(b.name)
    })
  }, [inputs, metaByCid])

  return { entries, hasEnriched }
}
